import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, Zap, Activity, RefreshCw, TrendingUp, AlertCircle } from 'lucide-react';
import { dashboardAPI, syncAPI } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import ActivityJournal from '@/components/ActivityJournal';

const BAR_COLORS = [
  '#E8452E', '#c73a26', '#ff6b52', '#2A9D8F', '#22C55E',
  '#16A34A', '#3B82F6', '#8B5CF6', '#F59E0B', '#6B7280',
];

const StatusDistributionChart = ({ distribution, total }) => {
  // Sort by count descending
  const sorted = Object.entries(distribution).sort((a, b) => b[1] - a[1]);
  const TOP_N = 10;
  const top = sorted.slice(0, TOP_N);
  const othersCount = sorted.slice(TOP_N).reduce((sum, [, c]) => sum + c, 0);
  const othersStatuts = sorted.length - TOP_N;

  const maxCount = top[0]?.[1] || 1;

  return (
    <div className="space-y-2.5" data-testid="status-distribution-chart">
      {top.map(([status, count], i) => {
        const pct = Math.round((count / total) * 100);
        const barW = Math.round((count / maxCount) * 100);
        return (
          <div key={status} className="flex items-center gap-3">
            <span
              className="text-xs font-medium text-gray-600 truncate"
              style={{ minWidth: 80, maxWidth: 120 }}
              title={status}
            >
              {status}
            </span>
            <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${barW}%`, backgroundColor: BAR_COLORS[i % BAR_COLORS.length] }}
              />
            </div>
            <span className="text-xs font-semibold text-gray-700 w-10 text-right whitespace-nowrap">
              {count} <span className="text-gray-400 font-normal">({pct}%)</span>
            </span>
          </div>
        );
      })}
      {othersStatuts > 0 && (
        <div className="flex items-center gap-3 pt-1 border-t border-dashed border-gray-200">
          <span className="text-xs font-medium text-gray-400" style={{ minWidth: 80, maxWidth: 120 }}>
            +{othersStatuts} autres
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="h-2.5 rounded-full transition-all duration-500 bg-gray-300"
              style={{ width: `${Math.round((othersCount / maxCount) * 100)}%` }}
            />
          </div>
          <span className="text-xs font-semibold text-gray-500 w-10 text-right whitespace-nowrap">
            {othersCount}
          </span>
        </div>
      )}
      <p className="text-xs text-gray-400 pt-1">
        Top {Math.min(TOP_N, sorted.length)} sur {sorted.length} statut{sorted.length > 1 ? 's' : ''} · {total} clients au total
      </p>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_clients: 0,
    active_triggers: 0,
    total_actions: 0,
    status_distribution: {},
  });
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    try {
      const response = await dashboardAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await syncAPI.syncGoogleSheets();
      toast.success(`Synchronisation terminée ! ${response.data.clients_added} clients ajoutés.`);
      fetchStats();
    } catch (error) {
      toast.error('Échec de la synchronisation. Veuillez réessayer.');
    } finally {
      setSyncing(false);
    }
  };

  const statCards = [
    {
      title: 'Total Clients',
      value: stats.total_clients,
      icon: Users,
      color: 'hsl(var(--foreground))',
      bgColor: 'hsl(var(--muted))',
      accentColor: 'hsl(var(--primary))',
    },
    {
      title: 'Automatisations actives',
      value: stats.active_triggers,
      icon: Zap,
      color: 'hsl(var(--primary))',
      bgColor: 'hsl(var(--primary) / 0.12)',
      accentColor: 'hsl(var(--primary))',
    },
    {
      title: 'Actions envoyées',
      value: stats.total_actions,
      icon: Activity,
      color: 'hsl(var(--success))',
      bgColor: 'hsl(var(--success) / 0.12)',
      accentColor: 'hsl(var(--success))',
    },
    {
      title: 'Taux de succès',
      value: '98%',
      icon: TrendingUp,
      color: 'hsl(var(--success))',
      bgColor: 'hsl(var(--success) / 0.12)',
      accentColor: 'hsl(var(--success))',
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6 sm:space-y-8"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold font-heading tracking-tight text-foreground">
            Dashboard
          </h1>
          <p className="text-gray-600 mt-2 leading-relaxed text-sm sm:text-base">
            Suivez et automatisez vos relances clients
          </p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          className="bg-primary text-white hover:bg-primary/90 rounded-lg px-4 sm:px-5 py-2 font-medium text-sm w-full sm:w-auto"
          data-testid="sync-button"
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Synchronisation...' : 'Synchroniser Google Sheets'}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">{statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="border border-border/60 hover:shadow-md transition-shadow duration-200">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {stat.title}
                    </p>
                    <p className="text-2xl sm:text-3xl font-bold font-heading mt-2" style={{ color: stat.accentColor }}>
                      {loading ? '...' : stat.value}
                    </p>
                  </div>
                  <div className="p-2.5 rounded-lg" style={{ backgroundColor: stat.bgColor }}>
                    <stat.icon size={20} style={{ color: stat.accentColor }} strokeWidth={1.5} className="sm:w-6 sm:h-6" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Journal d'activité — ce qui s'est réellement passé, et les réactions
          clients. C'est le cœur de la page d'accueil : on regarde d'abord les
          faits, les statistiques viennent ensuite. */}
      <div className="space-y-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold font-heading tracking-tight text-foreground">
            Activité récente
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Chaque appel, email et SMS envoyé — cliquez une ligne pour le détail.
          </p>
        </div>
        <ActivityJournal defaultPeriod="7d" />
      </div>

      <Card className="border border-border/60">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-xl">Répartition des Statuts</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500 text-sm">Chargement...</p>
          ) : Object.keys(stats.status_distribution).length > 0 ? (
            <StatusDistributionChart distribution={stats.status_distribution} total={stats.total_clients} />
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-gray-500">Aucun client. Synchronisez pour commencer !</p>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default Dashboard;